import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import {
	fetchAstrositeById,
	fetchAstrositeStates,
	selectAstrositesById,
	selectAstrositeStatesByContext,
	selectAstrositeStatesByIds,
	updateAstrositeAutoActivation,
} from "./slice";
import NavMain from "../../app/NavMain";
import Layout from "../../ui/Layout";
import ScrollablePage from "../../ui/ScrollablePage";
import Paper from "../../ui/Paper";
import MapPin from "../geography/MapPin";
import AstrositeMapLabel from "./AstrositeMapLabel";
import WindyWidget from "../weather/WindyWidget";
import { selectAdvancedMode } from "../settings/slice";
import { useInterval } from "../timing/use-interval";
import AstrositeStatus from "./AstrositeStatus";
import { Header, Overview, Actions, Action, Facts } from "../../ui/PageHeader";

// import MoonPhase from "../schedules/MoonPhase"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner";

import YRWidget from "../weather/YRWidget";
import TelematicsLineChart from "./TelematicsLineChart";
import mpsToKmph from "../../conversions/mpsToKmph";
import Button from "../../ui/Button";
import { faPlay } from "@fortawesome/free-solid-svg-icons";

import ErrorMsg from "../../ui/ErrorMsg";
import Timestamp from "../../ui/Timestamp";

import PropTypes from "prop-types";
import PaperTitle, { TitleText } from "../../ui/PaperTitle";
import {
	fetchTimelapses,
	selectTimelapsesByContext,
	selectTimelapsesByIds,
} from "../timelapses/slice";
import TimelapsesTable from "../timelapses/TimelapsesTable";
import ToggleSwitch from "../../ui/ToggleSwitch";
import Dialog from "../../ui/Dialog";
import Authed from "../iam/Authed";
import AllSkyVideo from "../timelapses/AllSkyVideo";
import Breadcrumbs from "../../ui/Breadcrumbs";
import IDCopy from "../../ui/IDCopy";

const UPDATE_INTERVAL = 20000; // ms

// const SunMoonContainer = styled.div`
//   flex: auto;
//   display: grid;
//   grid-template-rows: repeat(3, 1fr);
//   padding: 2rem;
// `

const WeatherContainer = styled.div`
  display: grid;
  gap: 0.5rem 0.5rem;
  grid-template-columns: 1fr 1fr;
  height: 28rem;
`;

const TopContainer = styled.div`
  display: grid;
  gap: 0.5rem 0.5rem;
  grid-template-columns: 1fr 1fr;
  margin-bottom: 0.5rem;
  height: 100%;
  width: 100%;
`;

const TelematicsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-items: center;
`;

const ChartContainer = styled.div`
  flex: auto;
  max-height: 12rem;
`;

const ActivateContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Label = styled.span`
  margin-left: 8px; // Adjust as necessary to add spacing between the toggle and the text
`;

const ErrorSmall = styled.span`
  color: ${(props) => props.theme.colours.error};
  font-size: 0.8em;
`;

const AstrositeIcon = styled.div`
  display: inline-block;
  width: 1rem;
  height: 1rem;
  background-color: ${(props) => props?.color || "transparent"};
  margin-right: 1rem;
`;

const dataByName = (states = [], name) => {
	return states
		.map((state) => {
			return {
				x: new Date(state.timestamp),
				y: state[name],
			};
		})
		.filter((coord) => coord.y);
};

const Astrosite = () => {
	const { id } = useParams();
	const dispatch = useDispatch();
	const astrositesByID = useSelector(selectAstrositesById);
	// we get any matching states for this astrosite
	const astrositeStateMatches = useSelector((state) =>
		selectAstrositeStatesByContext(state, `astrosite/${id}`),
	);
	// We get the full objects from the ids the match
	const astrositeStates = useSelector((state) =>
		selectAstrositeStatesByIds(state, astrositeStateMatches?.ids || []),
	);

	const handleOpenGliaClick = () => {
		// FIXME: Retrieve Glia URL from server (make configurable)
		window.open(`http://172.20.172.6/#/${id}`, "_blank");
	};

	const astrosite = astrositesByID[id];

	// User setting to show more details such as id's
	const advancedMode = useSelector(selectAdvancedMode);

	const updateData = () => {
		dispatch(
			fetchAstrositeStates({
				filter: {
					astrosite_id: id,
				},
				page: {
					offset: 0,
					limit: 150,
				},
				context: `astrosite/${id}`,
			}),
		);
		// dispatch(fetchAstrositeSunInfo(id))
	};
	useEffect(() => {
		dispatch(fetchAstrositeById(id));
		// Get the latest state data for the astrosite
		updateData();
	}, [id]);

	// Poll at an interval
	useInterval(updateData, UPDATE_INTERVAL, false);

	const astrositeLocation = astrosite &&
		astrosite.location && {
			id: astrosite.id,
			content: <AstrositeMapLabel astrosite={astrosite} />,
			latitude: astrosite.location.latitude_degrees,
			longitude: astrosite.location.longitude_degrees,
		};

	return (
		<Layout>
			<NavMain />
			<Helmet>
				<title>Astrosite {id} </title>
			</Helmet>
			{astrositeStates.length === 0 &&
				astrositeStateMatches?._loadStatus === "loading" && (
					<Backdrop>
						<LoadingSpinner />
					</Backdrop>
				)}
			<ScrollablePage>
				{/*  TODO add color for astrosite name */}
				<Header
					style={{
						borderLeft: `3px solid ${astrosite?.color || "transparent"}`,
						cursor: "pointer",
					}}
				>
					<Overview>
						<h2>
							<AstrositeIcon {...astrosite} />
							{astrosite?.name}
						</h2>
						<Breadcrumbs>
							<Link to="/astrosites">Astrosites</Link>
							<IDCopy id={astrosite?.id} showCopy />
						</Breadcrumbs>
						{!astrosite?.enabled &&
							astrositeStateMatches?._loadStatus !== "loading" && (
								<ErrorMsg
									msg={"Not In Service"}
									showIcon={true}
									showMsg={true}
								/>
							)}
					</Overview>
					{astrosite?.id && astrosite?._loadStatus !== "loading" && (
						<>
							<Facts>
								<dt>Status</dt>
								<dd>
									<AstrositeStatus
										astrosite={astrosite}
										advancedMode={advancedMode}
										hideLastUpdated={true}
										showStatusText={true}
										showIcon={false}
									/>
								</dd>
								<dt>System state</dt>
								<dd>{astrosite?.deploy_state?.toLowerCase()}</dd>
								<dt>Last updated</dt>
								<dd>
									<Timestamp utc={advancedMode} columnMode={false}>
										{astrosite?.lastUpdated}
									</Timestamp>
								</dd>
								<dt>Network address</dt>
								<dd>
									<IDCopy id={astrosite?.internal_ip} showCopy />
								</dd>
								<dt>UUID</dt>
								<dd>
									{astrosite?.external_uuid ? (
										<IDCopy
											id={astrosite?.external_uuid}
											showCopy
											truncateTo={16}
										/>
									) : (
										<ErrorSmall>Not Set</ErrorSmall>
									)}
								</dd>
							</Facts>
							<Facts>
								<dt>Automatic activation</dt>
								<dd>{astrosite?.auto_activate ? "on" : "off"}</dd>
								<dt>Weather override</dt>
								<dd>{astrosite?.weather_override ? "on" : "off"}</dd>
								{/*
            <dt>Scope room lighting</dt>
            <dd>{astrosite?.power?.light ? "on" : "off"}</dd>
            <dt>Dew heater</dt>
            <dd>{astrosite?.power?.dew ? "on" : "off"}</dd>
            */}
								<dt>Last PAT sent</dt>
								<dd>
									{/* TODO: If older than X, or never, highlight as error*/}
									{astrosite?.last_pat_sent != null ? (
										<Timestamp utc={advancedMode} columnMode={false}>
											{astrosite?.last_pat_sent}
										</Timestamp>
									) : (
										<ErrorMsg msg="never" />
									)}
								</dd>
								<dt>Sunrise at site</dt>
								<dd>
									{astrosite?.daylight_today?.sunrise ? (
										<Timestamp utc={advancedMode} columnMode={false}>
											{astrosite?.daylight_today?.sunrise}
										</Timestamp>
									) : (
										<ErrorMsg msg="unknown" />
									)}
								</dd>
								<dt>Sunset at site</dt>
								<dd>
									{astrosite?.daylight_today?.sunset ? (
										<Timestamp utc={advancedMode} columnMode={false}>
											{astrosite?.daylight_today?.sunset}
										</Timestamp>
									) : (
										<ErrorMsg msg="unknown" />
									)}
								</dd>
							</Facts>
						</>
					)}
					<Actions>
						<Authed allowRoles={["admin", "deployer"]}>
							<Action>
								<Button icon={faPlay} onClick={handleOpenGliaClick}>
									Open Glia control panel
								</Button>
							</Action>
						</Authed>
						<Authed allowRoles={["admin"]}>
							<Action>
								<AstrositeActivationToggle astrosite={astrosite} />
							</Action>
						</Authed>
					</Actions>
				</Header>
				{astrositeStateMatches?._loadStatus === "rejected" && (
					<ErrorMsg
						msg={"There was a problem fetching the Astrosite info"}
						showIcon={true}
					/>
				)}
				{astrositeStates?.length > 0 && (
					<>
						<Paper>
							<TopContainer>
								{astrositeLocation && (
									<MapPin
										pins={[astrositeLocation]}
										width="100%"
										height="30rem"
									/>
								)}
								<AllSkyVideo astrosite_id={id} />
								{/* TODO: Move to top "facts" once data wired up correctly again -
              <Paper>
                <SunInfo
                  sunrise_utc={sunInfo?.sun_rise}
                  sunset_utc={sunInfo?.sun_set}
                />
              </Paper>
              */}
							</TopContainer>
						</Paper>
						<TelematicsContainer>
							<ChartContainer>
								<TelematicsLineChart
									data={(
										dataByName(astrositeStates, "wind_metres_per_second") || []
									).map((d) => ({ x: d.x, y: mpsToKmph(d.y) }))}
									title={"Wind speed (km/h)"}
									unit={"m/s"}
									xLabel={"time"}
									yLabel={"km/h"}
								/>
							</ChartContainer>
							<ChartContainer>
								<TelematicsLineChart
									data={
										dataByName(
											astrositeStates,
											"sky_minus_ambient_temperature_celsius",
										) || []
									}
									title={"Sky temp minus ambient temp (celsius)"}
									unit={"celsius"}
									xLabel={"time"}
									yLabel={"celsius"}
								/>
							</ChartContainer>
							<ChartContainer>
								<TelematicsLineChart
									data={(
										dataByName(astrositeStates, "relative_humidity") || []
									).map((d) => ({ x: d.x, y: d.y * 100 }))}
									title={"Relative humidity"}
									unit={"percent"}
									xLabel={"time"}
									yLabel={"percent"}
								/>
							</ChartContainer>
							<ChartContainer>
								<TelematicsLineChart
									data={
										dataByName(
											astrositeStates,
											"ambient_temperature_celsius",
										) || []
									}
									title={"Ambient temp"}
									unit={"celsius"}
									xLabel={"time"}
									yLabel={"celsius"}
								/>
							</ChartContainer>
						</TelematicsContainer>
					</>
				)}

				<Paper>
					<WeatherContainer>
						{astrositeLocation && (
							<WindyWidget
								height="30rem"
								latitude={astrositeLocation.latitude}
								longitude={astrositeLocation.longitude}
							/>
						)}
						{astrositeLocation && (
							<YRWidget
								latitude={astrositeLocation?.latitude}
								longitude={
									astrositeLocation?.longitude > 180
										? astrositeLocation.longitude - 360
										: astrositeLocation.longitude
								}
							/>
						)}
					</WeatherContainer>
				</Paper>
				{/* TODO example usage of timelapses */}
				{/*<AstrositeAllSkyTimelapses astrosite={id} />*/}
				{/*<TimelapseVideo id={"1a3c19c7f161f49cc9dba9dbe7eeb091"} />*/}
				{/*<UpcomingActivePast astrosite={id} />*/}
			</ScrollablePage>
		</Layout>
	);
};

const AstrositeActivationToggle = ({ astrosite = {} }) => {
	const [isOpen, setIsOpen] = useState(false);
	const dispatch = useDispatch();

	const openModal = () => {
		setIsOpen(true);
	};

	const closeModal = () => {
		setIsOpen(false);
	};

	const handleActivate = (activate = false, astrosite = {}) => {
		dispatch(
			updateAstrositeAutoActivation({
				id: astrosite.id,
				_etag: astrosite._etag,
				autoActivate: activate,
			}),
		);
		closeModal();
	};

	return (
		<div>
			{astrosite._saveStatus === "updating" && (
				<Backdrop>
					<LoadingSpinner />
				</Backdrop>
			)}
			<ActivateContainer>
				<ToggleSwitch
					handleChange={openModal}
					id="mode"
					checked={astrosite?.auto_activate}
				/>
				<Label>Activate automatically</Label>
			</ActivateContainer>
			<Dialog
				isOpen={isOpen}
				onRequestClose={closeModal}
				contentLabel="Activation Confirmation"
			>
				<h2>Confirmation</h2>
				<p>
					Are you sure you want to{" "}
					{astrosite?.auto_activate ? "de-activate" : "activate"}{" "}
					{astrosite?.id}?
				</p>

				{astrosite?.auto_activate ? (
					<p>Stop automatically activating the Astrosite</p>
				) : (
					<div>
						<p>
							The astrosite will automatically activate itself when conditions
							are favourable (after Sunset, good weather, etc).
						</p>
						<p>
							Warning: For your safety please make sure you are never in the
							Astrosite container while this is enabled!
						</p>
					</div>
				)}

				<Button
					onClick={() => handleActivate(!astrosite?.auto_activate, astrosite)}
				>
					Yes
				</Button>
				<Button onClick={closeModal}>No</Button>
			</Dialog>
			{astrosite?._saveStatus === "rejected" && (
				<ErrorMsg msg={"Failed to update auto-activation"} showIcon={true} />
			)}
		</div>
	);
};
AstrositeActivationToggle.propTypes = {
	astrosite: PropTypes.object,
};

// Renders a table of timelapse video descriptions for this Astrosaite
const AstrositeAllSkyTimelapses = ({ astrosite }) => {
	const dispatch = useDispatch();

	// we get any matching collections for this Observation
	const timelapseMatches = useSelector((state) =>
		selectTimelapsesByContext(state, `astrosite/${astrosite}`),
	);
	// We get the full objects from the ids the match
	const timelapses = useSelector((state) =>
		selectTimelapsesByIds(state, timelapseMatches?.ids || []),
	);

	useEffect(() => {
		// get latest 12 video descriptors
		dispatch(
			fetchTimelapses({
				filter: {
					astrosite,
					sort_by: "start_time",
					sort_order: "DESC",
				},
				page: {
					offset: 0,
					limit: 12,
				},
				context: `astrosite/${astrosite}`,
			}),
		);
	}, [astrosite]);

	return (
		<Paper>
			<PaperTitle>
				<TitleText loadingState={timelapseMatches?._loadStatus || "idle"}>
					Latest All Sky timelapse videos
				</TitleText>
			</PaperTitle>

			{timelapseMatches?._loadStatus === "rejected" && (
				<div>Problem loading the All Sky timelapse videos</div>
			)}
			<TimelapsesTable
				timelapses={timelapses}
				stickyHeader={false}
				hideFilters={true}
			/>
		</Paper>
	);
};

AstrositeAllSkyTimelapses.propTypes = {
	astrosite: PropTypes.string, // id of the Astrosite fdor which we want timelapses
};

export default Astrosite;
